import React from "react";
import styled, { css } from "styled-components";
import {
  IStyles,
  ITheButton,
  ITheTableCell,
  TTheTableCellProps,
  TTheTableRowProps,
  TheButton,
  TheTable,
} from "components";
import {
  IDtoLeaderboard,
  IDtoLeaderboardUser,
  LeaderboardPrizeSelectionType,
  TheLeaderboardSourceKeys,
} from "interfaces";
import { PRIZES_OUTSIDE_ADDITIONAL_PRIZES_COUNT } from "helpers/constants";
import { Flex } from "components/common";
import { formatNumber } from "./utils/formatNumbers";
import CurrencyNumbers from "components/wrewards-ui-kit/CurrencyNumbers";
import { LeaderboardRaffleVerifyModalButton } from "./LeaderboardRaffleVerifyModal";

export type ILeaderboardUserTableItem = IDtoLeaderboardUser & {
  prize: number;
  prizeColor: string;
  prizeId?: string;
  rowIndex: number;
};

export type ILeaderboardUserTableOptions = {};

export interface ILeaderboardUsersTableProps {
  $keys?: string[];
  leaderboard?: IDtoLeaderboard | null;
  leaderboardUsers: IDtoLeaderboardUser[];
  usersSkip?: number;
  usersTake?: number;
  selectedTab?: string;
  onBan?: (item: ILeaderboardUserTableItem, rowId: number) => void;
  onRepickAttempt?: ({ prizeId }: { prizeId: string }) => void;
  onUnban?: (item: ILeaderboardUserTableItem, rowId: number) => void;
  isSingleUser?: boolean;
  // showTicketsColumn: boolean;
  ticketRate?: number | null;
}

export const LeaderboardUsersTable: React.FC<ILeaderboardUsersTableProps> = ({
  $keys = [
    "#",
    "refereeId",
    "displayName",
    "value",
    "prize",
    "randomPrize",
    "bann",
    "repick",
    "prizeId",
    "tickets",
  ],
  leaderboard,
  leaderboardUsers,
  usersSkip = 0,
  selectedTab,
  onBan,
  onUnban,
  onRepickAttempt,
  isSingleUser,
  ticketRate,
}): React.ReactElement | null => {
  const setup = React.useMemo(
    () => [
      {
        $key: "#",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          // return <RowId>{props.row?.position || usersSkip + props.rowId + 1}</RowId>;
          const place = isSingleUser ? props.row?.position : usersSkip + props.rowId + 1;
          return (
            <RowId className="place">
              <span className="text">{place}</span>
            </RowId>
          );
        },
        title: null,
      },
      {
        $key: "place-displayName",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const _: string | undefined = props.row?.displayName;
          const place = isSingleUser ? props.row?.position : usersSkip + props.rowId + 1;
          return (
            <Flex gap={24} align="center">
              <RowId className={`place place-${place}`}>
                <span className="text">{place}</span>
              </RowId>
              <User>{_ ? _ : "TBD"}</User>
            </Flex>
          );
        },
        title: <Title>Place & User</Title>,
      },
      {
        $key: "refereeId",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const refereeId: string | null = props.cell;
          return (
            <Title style={{ color: "#d2eaff" }}>
              {refereeId === "-1" ? "HIDDEN" : refereeId || "TBD"}
            </Title>
          );
        },
        title: <Title>ID</Title>,
      },
      {
        $key: "displayName",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const _: string | undefined = props.cell;
          return <User>{_ ? _ : "TBD"}</User>;
        },
        title: <Title>User</Title>,
      },
      {
        $key: "value",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const casinoProvider: string | undefined = leaderboard?.casinoProvider;

          switch (casinoProvider) {
            case TheLeaderboardSourceKeys.gamdom:
            case TheLeaderboardSourceKeys.packdraw:
            case TheLeaderboardSourceKeys.clash:
            case TheLeaderboardSourceKeys.cases:
            case TheLeaderboardSourceKeys.rain:
            case TheLeaderboardSourceKeys.luxdrop:
            case TheLeaderboardSourceKeys.lootbox:
            case TheLeaderboardSourceKeys.datdrop:
              return (
                <Flex width="100%" justify="center">
                  <CurrencyNumbers icon="USD">
                    {formatNumber(props.row?.wageredTotal ? +props.row.wageredTotal : 0)}
                  </CurrencyNumbers>
                </Flex>
              );
            case TheLeaderboardSourceKeys.hypedrop:
              return (
                <Flex width="100%" justify="center">
                  <CurrencyNumbers icon="USD">
                    {formatNumber(props.row?.deposited ? +props.row?.deposited : 0)}
                  </CurrencyNumbers>
                </Flex>
              );
            case TheLeaderboardSourceKeys.wrewards:
              return (
                <Flex width="100%" justify="center">
                  <CurrencyNumbers icon="GCoin">
                    {formatNumber(props.row?.wageredTotal ? +props.row.wageredTotal : 0)}
                  </CurrencyNumbers>
                </Flex>
              );

            default:
              return null;
          }
        },
        title: () => {
          const casinoProvider: string | undefined = leaderboard?.casinoProvider;

          switch (casinoProvider) {
            case TheLeaderboardSourceKeys.gamdom:
            case TheLeaderboardSourceKeys.packdraw:
            case TheLeaderboardSourceKeys.clash:
            case TheLeaderboardSourceKeys.cases:
            case TheLeaderboardSourceKeys.rain:
            case TheLeaderboardSourceKeys.luxdrop:
            case TheLeaderboardSourceKeys.lootbox:
            case TheLeaderboardSourceKeys.datdrop:
              return (
                <Title
                  styles={css`
                    text-align: center;
                  `}
                >
                  Wagered
                </Title>
              );
            case TheLeaderboardSourceKeys.hypedrop:
              return (
                <Title
                  styles={css`
                    text-align: center;
                  `}
                >
                  Deposited
                </Title>
              );
            case TheLeaderboardSourceKeys.wrewards:
              return (
                <Title
                  styles={css`
                    text-align: center;
                  `}
                >
                  Points
                </Title>
              );

            default:
              return null;
          }
        },
      },

      {
        $key: "tickets",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const calculatedTickets =
            props.row?.wageredTotal && ticketRate
              ? Math.floor(Number(props.row?.wageredTotal) / ticketRate)
              : Math.floor(Number(props.row?.wageredTotal));

          return (
            <Flex width="100%" justify="flex-end">
              <CurrencyNumbers icon="Raffle">{formatNumber(calculatedTickets)}</CurrencyNumbers>
            </Flex>
          );
        },
        title: (
          <Title
            styles={css`
              text-align: right;
            `}
          >
            Tickets
          </Title>
        ),
      },

      {
        $key: "prize",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const row: ILeaderboardUserTableItem | null = props.row;

          return (
            <Flex justify="flex-end" align="center" gap={8}>
              <Prize style={row?.prizeColor ? { color: row.prizeColor } : {}}>
                {formatNumber(row?.prize ? +row.prize : 0)}
              </Prize>
              {props.row?.proof && <LeaderboardRaffleVerifyModalButton proof={props.row?.proof} />}
            </Flex>
          );
        },
        title: (
          <Title
            styles={css`
              text-align: right;
            `}
          >
            Prize
          </Title>
        ),
      },
      {
        $key: "bann",
        render: (props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>) => {
          const banned: boolean | undefined = props.row?.banned;
          const propsButton: IStyles & ITheButton = {
            preset: "dark-blue",
            width: null,
            height: null,
            styles: css`
              padding: 9px 18px;
            `,
          };
          if (banned === true) {
            const onClick = (event: React.MouseEvent) => {
              event.preventDefault();
              if (onUnban && props?.row) {
                onUnban(props.row, props.rowId);
              }
            };
            return (
              <TheButton {...propsButton} onClick={onClick}>
                Unban
              </TheButton>
            );
          } else if (banned === false) {
            const onClick = (event: React.MouseEvent) => {
              event.preventDefault();
              if (onBan && props?.row) {
                onBan(props.row, props.rowId);
              }
            };
            return (
              <TheButton {...propsButton} onClick={onClick}>
                Ban
              </TheButton>
            );
          }
          return null;
        },
        title: null,
      },
      {
        $key: "delete",
        render: () => {
          const propsButton: IStyles & ITheButton = {
            preset: "dark-blue",
            width: null,
            height: null,
            styles: css`
              padding: 9px 18px;
            `,
          };
          return <TheButton {...propsButton}>Delete</TheButton>;
        },
        title: null,
      },
      ...(selectedTab === "random"
        ? [
            {
              $key: "repick",
              render: (
                props: ITheTableCell<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>
              ) => {
                const propsButton: IStyles & ITheButton = {
                  preset: "dark-blue",
                  width: 150,
                  height: 30,
                  styles: css`
                    padding: 9px 18px;
                  `,
                };
                const isRowHasDisplayName: boolean = !!props.row?.displayName;
                const prizeId: string | undefined = props.row?.prizeId;

                return isRowHasDisplayName ? (
                  <Flex width="100%" justify="flex-end">
                    <TheButton
                      onClick={(e) => {
                        e.preventDefault();
                        if (onRepickAttempt && prizeId) {
                          onRepickAttempt({ prizeId });
                        }
                      }}
                      {...propsButton}
                    >
                      Repick
                    </TheButton>
                  </Flex>
                ) : null;
              },
              title: null,
            },
          ]
        : []),
    ],
    [
      usersSkip,
      leaderboard,
      onBan,
      onUnban,
      selectedTab,
      onRepickAttempt,
      ticketRate,

      // ,  showTicketsColumn
    ]
  );

  const rows = leaderboardUsers?.length
    ? leaderboardUsers.map((user: IDtoLeaderboardUser, index: number) => {
        const additionalPrizes = leaderboard?.additionalPrizes || [];
        const userIndex = isSingleUser && user.position ? user.position - 1 : usersSkip + index;
        const rowIndex = userIndex + 1;

        let prize = 0;
        let prizeColor = "#5ebd3d";

        if (userIndex === 0) {
          prize = leaderboard?.prize1 || 0;
        } else if (userIndex === 1) {
          prize = leaderboard?.prize2 || 0;
        } else if (userIndex === 2) {
          prize = leaderboard?.prize3 || 0;
        } else if (userIndex < additionalPrizes.length + PRIZES_OUTSIDE_ADDITIONAL_PRIZES_COUNT) {
          prize = additionalPrizes[userIndex - 3].amount || 0;
        }

        if (user.randomPrize) {
          prize = +user.randomPrize;
          prizeColor = "#3984dd";
        }

        return {
          ...user,
          prize,
          prizeColor,
          rowIndex,
        };
      })
    : [];

  const cellProps: TTheTableCellProps<ILeaderboardUserTableItem, ILeaderboardUserTableOptions> = ({
    isHead,
    isBody,
    $key,
    row,
  }) => {
    if (isHead) {
      return ["#", "bann"].includes($key) ? { styles: cssCell } : {};
    } else if (isBody) {
      return row?.banned ? { styles: cssBannedCell } : {};
    }
    return {};
  };

  const rowProps: TTheTableRowProps<ILeaderboardUserTableItem, ILeaderboardUserTableOptions> = ({
    isBody,
    row,
  }) => {
    if (row?.rowIndex === 1) {
      return {
        styles: css`
          background: linear-gradient(90deg, rgba(226, 192, 86, 0.2) 0%, rgba(0, 0, 0, 0) 100%);
          background-repeat: no-repeat;
          border: none;
          overflow: hidden;
        `,
      };
    }
    if (row?.rowIndex === 2) {
      return {
        styles: css`
          background: linear-gradient(
            90deg,
            rgba(198, 202, 216, 0.2) 0%,
            rgba(198, 202, 216, 0) 100%
          );
          background-repeat: no-repeat;
          border: none;
          overflow: hidden;
        `,
      };
    }
    if (row?.rowIndex === 3) {
      return {
        styles: css`
          background: linear-gradient(
            90deg,
            rgba(224, 171, 126, 0.2) 0%,
            rgba(224, 171, 126, 0) 100%
          );
          background-repeat: no-repeat;
          border: none;
          overflow: hidden;
        `,
      };
    }

    if (isBody) {
      return row?.banned ? { styles: cssBannedRow } : {};
    }

    return {};
  };

  return (
    <Box isHiddenHead={!!isSingleUser}>
      {/* {isSingleUser && ( */}
      <Title
        styles={css`
          position: relative;
          text-align: center;
          /* top: 24px; */
        `}
      >
        {isSingleUser
          ? "Your Position"
          : leaderboard?.prizeSelectionType === LeaderboardPrizeSelectionType.RAFFLE
            ? "WAGER TICKET LEADERS"
            : "Other Leaders"}
      </Title>
      {/* )} */}
      <TheTable<ILeaderboardUserTableItem, ILeaderboardUserTableOptions>
        $keys={$keys}
        $setup={setup}
        rows={rows}
        CellProps={cellProps}
        RowProps={rowProps}
        // @ts-ignore
        styles={{ overflow: "visible" }}
        boxStyles={css`
          overflow: auto;
        `}
      />
    </Box>
  );
};

//  TO DO LOADING RING

const cssCell = css`
  width: 1px;
`;

const cssBannedCell = css`
  border-top: 1px solid red;
  border-bottom: 1px solid red;
  &:first-child {
    border-left: 1px solid red;
  }
  &:last-child {
    border-right: 1px solid red;
  }
`;

const cssBannedRow = css``;

const Box = styled.div<IStyles & { isHiddenHead: boolean }>`
  box-sizing: border-box;
  text-align: left;
  position: relative;
  margin-bottom: 1rem;
  thead {
    visibility: ${({ isHiddenHead }) => (isHiddenHead ? "hidden" : "visible")};
  }

  thead td {
    text-transform: uppercase;
    padding: 0.25rem 1.5rem;
  }

  td {
    padding: 0.75rem 0.75rem;
  }

  tr td:not(:first-child, :last-child) {
    padding: 0.75rem 0.25rem;
  }

  @media (min-width: 800px) {
    td {
      height: 56px;
    }
    tr td:nth-child(2) {
      min-width: 300px;
    }
    tr td:nth-child(4) {
      min-width: 200px;
    }
  }
  @media (min-width: 1024px) {
    td {
      padding: 0.75rem 1.5rem;
    }
  }

  ${({ styles }) => styles}
`;

const RowId = styled.div<IStyles>`
  /* box-sizing: border-box; */
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 10px;
  height: 10px;
  padding: 8px;
  position: relative;
  /* left: -32px; */

  &::after {
    content: "";
    position: absolute;
    z-index: 0;
    width: 24px;
    height: 24px;
    top: 0;
    background: linear-gradient(130deg, #141722 27.86%, #1b2030 95.32%);
    transform: rotate(45deg) translate(-5px, -5px);
    border-radius: 4px;
    border: 2px solid var(--daylight-daylight-75, rgba(200, 215, 245, 0.75));
  }

  &.place-1 {
    &::after {
      content: "";
      position: absolute;
      z-index: 0;
      width: 24px;
      height: 24px;
      top: -4px;
      transform: rotate(45deg);
      border-radius: 4px;
      background: rgba(226, 192, 86, 0.25);
      border: 2px solid #e2c056;
    }
  }
  &.place-2 {
    &::after {
      content: "";
      position: absolute;
      z-index: 0;
      width: 24px;
      height: 24px;
      top: -4px;
      transform: rotate(45deg);
      border-radius: 4px;
      background: rgba(198, 202, 216, 0.25);
      border: 2px solid #c6cad8;
    }

    .text {
      color: #e2c056;
    }
  }
  &.place-3 {
    &::after {
      content: "";
      position: absolute;
      z-index: 0;
      width: 24px;
      height: 24px;
      top: -4px;
      transform: rotate(45deg);
      border-radius: 4px;
      background: rgba(224, 171, 126, 0.25);
      border: 2px solid #e0ab7e;
    }
  }

  .text {
    font-size: 16px;
    font-style: italic;
    font-weight: 800;
    position: relative;
    z-index: 2;
    background: linear-gradient(180deg, #e9f0ff 0%, #bdccfa 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 800px) {
    left: 0px;
  }
  /* 
  background: rgba(35, 163, 255, 0.05);
  border: 2px solid #0b101b;
  border-radius: 41px; */

  /* ${({ styles }) => styles} */
`;

const Title = styled.div<IStyles>`
  color: rgba(210, 234, 255, 0.75);
  color: var(--daylight-daylight-main, #d5e0f5);
  font-family: var(--font-family-exo);
  font-size: 20px;
  font-style: italic;
  font-weight: 800;
  line-height: normal;
  text-align: left;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.25);
  text-transform: uppercase;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.25);

  ${({ styles }) => styles}
`;

const User = styled.div<IStyles>`
  color: #d2eaff;
  font-family: var(--font-family-exo);
  font-size: 16px;
  font-style: italic;
  font-weight: 800;
  text-transform: uppercase;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.25);

  ${({ styles }) => styles}
`;

const Prize = styled.div<IStyles & { isRandom?: boolean }>`
  /* display: flex;
  align-items: center;
  justify-content: flex-end; */

  color: #5ebd3d;
  color: var(--daylight-daylight-main, #d5e0f5);
  font-family: var(--font-family-exo);
  font-size: 16px;
  font-style: italic;
  font-weight: 800;
  line-height: normal;
  letter-spacing: 0.32px;
  text-align: right;
  text-shadow: 0px 1px 3px rgba(0, 0, 0, 0.25);

  &::before {
    content: "$";
  }

  ${({ styles }) => styles}
`;
